import React from 'react';
import type {
  CoverageCellState,
  PostureGroup,
  PostureInstance,
  PostureResponse
} from './types';

interface StateTabProps {
  posture: PostureResponse;
  exporting: 'csv' | 'json' | null;
  onExport: (format: 'csv' | 'json') => void;
}

const prettyHost = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
};

const cellStateFor = (instance: PostureInstance, group: PostureGroup): CoverageCellState => {
  if (!instance.inspectable || instance.error) return 'not-checked';
  if (!group.exists) {
    return group.critical ? 'critical-missing' : 'missing';
  }
  return group.memberCount > 0 ? 'filled' : 'empty';
};

const cellGlyphFor = (state: CoverageCellState): string => {
  if (state === 'filled') return '✓';
  if (state === 'empty') return '◯';
  if (state === 'missing') return '✕';
  if (state === 'critical-missing') return '⚠';
  return '—';
};

const cellAriaLabelFor = (state: CoverageCellState): string => {
  if (state === 'filled') return 'group has members';
  if (state === 'empty') return 'group exists but is empty';
  if (state === 'missing') return 'group is missing';
  if (state === 'critical-missing') return 'critical group is missing';
  return 'site was not checked';
};

interface CellPanel {
  instanceId: string;
  groupKey: string;
}

/**
 * Tab 1 of /permissions per the design doc.
 *
 * The coverage grid is the primary affordance: rows are the Edinshall
 * security groups, columns are the tenant's inspectable installations.
 * Each cell answers "does this group exist on this site, and does it
 * have members?" in one glyph. Clicking a cell opens a side panel with
 * the member count, the expected members for that site, and any
 * expected members missing from the site. Actual member names are not
 * yet returned by the posture endpoint; the panel surfaces what the
 * endpoint exposes today.
 *
 * The baseline compliance band sits above the grid and only renders
 * when the tenant policy has enforceSystemAdmins on. It uses the
 * per-instance missingExpectedMembersByGroupKey.sysAdmin signal so it
 * reflects the site-vs-policy diff, not a policy-vs-policy compare.
 */
export const StateTab: React.FC<StateTabProps> = ({ posture, exporting, onExport }) => {
  const { policy, summary, instances } = posture;
  const [hideDeregistered, setHideDeregistered] = React.useState(true);
  const [openCell, setOpenCell] = React.useState<CellPanel | null>(null);

  const visibleInstances = React.useMemo(
    () => instances.filter((instance) => (hideDeregistered ? instance.status !== 'deregistered' : true)),
    [instances, hideDeregistered]
  );

  const groupCoverage = summary.groupCoverage;

  const baselineBand = React.useMemo(() => {
    if (!policy.enforceSystemAdmins || policy.baselineSystemAdmins.length === 0) {
      return null;
    }
    // Use the backend's actual missing-member signal — the posture
    // service returns missingExpectedMembersByGroupKey.sysAdmin per
    // instance, which is the difference between policy expectation
    // and what the site really has. Earlier draft compared the policy
    // against itself, which trivially passed.
    const failing: Array<{ instance: PostureInstance; missing: string[] }> = [];
    for (const instance of visibleInstances) {
      if (!instance.inspectable || instance.error) continue;
      const missing = instance.missingExpectedMembersByGroupKey.sysAdmin ?? [];
      if (missing.length > 0) {
        failing.push({ instance, missing });
      }
    }
    return failing;
  }, [policy.enforceSystemAdmins, policy.baselineSystemAdmins, visibleInstances]);

  const cellDetail = React.useMemo(() => {
    if (!openCell) return null;
    const instance = visibleInstances.find((entry) => entry.instanceId === openCell.instanceId);
    if (!instance) return null;
    const group = instance.groups.find((entry) => entry.key === openCell.groupKey);
    if (!group) return null;
    const expected = instance.expectedMembersByGroupKey[openCell.groupKey] ?? [];
    const missing = instance.missingExpectedMembersByGroupKey[openCell.groupKey] ?? [];
    return { instance, group, expected, missing };
  }, [openCell, visibleInstances]);

  return (
    <div className="permissions-state-tab">
      {/* Baseline band */}
      {baselineBand !== null && baselineBand.length === 0 && policy.enforceSystemAdmins && (
        <div className="permissions-baseline-band severity-success">
          <span className="band-icon" aria-hidden="true">✓</span>
          <span className="band-text">
            All inspectable sites satisfy the baseline System Administrators policy.
          </span>
        </div>
      )}
      {baselineBand !== null && baselineBand.length > 0 && (
        <div className="permissions-baseline-band severity-warning">
          <span className="band-icon" aria-hidden="true">⚠</span>
          <span className="band-text">
            <strong>{baselineBand.length} site{baselineBand.length === 1 ? '' : 's'}</strong>{' '}
            missing one or more baseline administrators.
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="permissions-state-toolbar">
        <label className="state-toolbar-toggle">
          <input
            type="checkbox"
            checked={hideDeregistered}
            onChange={(event) => setHideDeregistered(event.target.checked)}
          />
          <span>Hide deregistered</span>
        </label>
        <span className="hero-spacer" />
        <div className="state-toolbar-exports">
          <button
            className="btn secondary"
            type="button"
            onClick={() => onExport('csv')}
            disabled={exporting !== null}
          >
            {exporting === 'csv' ? 'Exporting CSV…' : 'Export CSV'}
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => onExport('json')}
            disabled={exporting !== null}
          >
            {exporting === 'json' ? 'Exporting JSON…' : 'Export JSON'}
          </button>
        </div>
      </div>

      {/* Coverage grid */}
      {visibleInstances.length === 0 ? (
        <div className="permissions-state-empty">
          No inspectable installations in this tenant yet.
        </div>
      ) : (
        <div className="permissions-coverage-wrap">
          <table className="permissions-coverage-grid" role="grid">
            <thead>
              <tr>
                <th scope="col" className="coverage-corner">Group</th>
                {visibleInstances.map((instance) => (
                  <th key={instance.instanceId} scope="col" className="coverage-col-head" title={instance.siteUrl}>
                    <div className="col-head-host">{prettyHost(instance.siteUrl)}</div>
                    {!instance.inspectable && <div className="col-head-note">not checked</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupCoverage.map((group) => (
                <tr key={group.key}>
                  <th scope="row" className={`coverage-row-head${group.critical ? ' is-critical' : ''}`}>
                    {group.name}
                    {group.critical && <span className="row-head-flag">critical</span>}
                  </th>
                  {visibleInstances.map((instance) => {
                    const live = instance.groups.find((entry) => entry.key === group.key);
                    if (!live) {
                      return (
                        <td key={`${instance.instanceId}-${group.key}`} className="coverage-cell not-checked">
                          <span aria-label="no data" title="No data">—</span>
                        </td>
                      );
                    }
                    const state = cellStateFor(instance, live);
                    const isSelected =
                      openCell?.instanceId === instance.instanceId && openCell?.groupKey === group.key;
                    return (
                      <td
                        key={`${instance.instanceId}-${group.key}`}
                        className={`coverage-cell ${state}${isSelected ? ' is-selected' : ''}`}
                      >
                        <button
                          type="button"
                          className="coverage-cell-button"
                          onClick={() => setOpenCell({ instanceId: instance.instanceId, groupKey: group.key })}
                          aria-label={`${group.name} on ${prettyHost(instance.siteUrl)}: ${cellAriaLabelFor(state)}`}
                          title={`${cellAriaLabelFor(state)} — ${live.memberCount} member${live.memberCount === 1 ? '' : 's'}`}
                        >
                          <span aria-hidden="true">{cellGlyphFor(state)}</span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cell detail panel */}
      {cellDetail && (
        <div className="permissions-cell-detail" role="region" aria-label="Cell detail">
          <div className="cell-detail-header">
            <strong>{cellDetail.group.name}</strong>
            <span className="muted">on {prettyHost(cellDetail.instance.siteUrl)}</span>
            <span className="hero-spacer" />
            <button className="btn secondary btn-small" type="button" onClick={() => setOpenCell(null)}>Close</button>
          </div>
          {cellDetail.instance.error && (
            <div className="muted">Inspection failed: {cellDetail.instance.error}</div>
          )}
          {!cellDetail.instance.error && !cellDetail.group.exists && (
            <div className="muted">This group does not exist on this site.</div>
          )}
          {!cellDetail.instance.error && cellDetail.group.exists && (
            <>
              <div className="cell-detail-row">
                <span className="cell-detail-label">Member count</span>
                <span className="cell-detail-value">
                  {cellDetail.group.memberCount}
                  {' '}<span className="muted">(names not exposed by the posture endpoint yet)</span>
                </span>
              </div>
              <div className="cell-detail-row">
                <span className="cell-detail-label">Expected by policy</span>
                <span className="cell-detail-value">
                  {cellDetail.expected.length === 0
                    ? <span className="muted">none configured</span>
                    : cellDetail.expected.join(', ')}
                </span>
              </div>
              {cellDetail.missing.length > 0 && (
                <div className="cell-detail-row">
                  <span className="cell-detail-label">Missing on site</span>
                  <span className="cell-detail-value cell-detail-warn">{cellDetail.missing.join(', ')}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StateTab;
